import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import ColumnLegendButton, { type ColumnHelp } from './ColumnLegendButton'
import HybridEvidenceCell from './HybridEvidenceCell'
import {
  formatGT,
  infoFlag,
  infoNumber,
  lrVaf,
  probandSampleName,
  sampleVaf,
  srVaf,
  tagBadgeClass,
  tagShortLabel,
  variantLocus,
  variantTag,
  type Variant,
} from './types'
import type { SortKey, VariantSort } from './variantQuery'

const ROW_HEIGHT = 28
const OVERSCAN = 12
// Load the next page when the user scrolls within this many px of the bottom.
const LOAD_MORE_THRESHOLD_PX = 400

// Shared column template so the sticky header and virtual rows stay aligned.
const GRID_TEMPLATE = 'minmax(120px,1.3fr) 100px minmax(60px,1fr) 56px 70px 46px 50px 46px 44px 64px 44px 56px 40px'
// Intrinsic width of the columns above (+ row px-3 padding). The header and body
// share a wrapper of this min width inside one scroll box, so the table clips to
// its own column and scrolls horizontally as a unit instead of the header
// spilling over the detail panel (GEN-3109 follow-up).
const TABLE_MIN_WIDTH = 820

type Column = {
  key: SortKey | null
  label: string
  /** One- to two-line definition; also the column's hover tooltip and legend text. */
  help: string
}

const COLUMNS: Column[] = [
  { key: 'locus', label: 'Locus', help: 'Genomic position (chrom:pos). The IGV icon opens this locus in IGV.' },
  { key: 'tag', label: 'TAG', help: 'LSMC confidence category (Confirmed -> ModelRejected).' },
  { key: 'ref', label: 'REF>ALT', help: 'Reference allele vs. alternate allele(s).' },
  { key: 'qual', label: 'QUAL', help: 'VCF call-quality score; higher = more confident.' },
  { key: 'filter', label: 'FILTER', help: 'VCF filter status (PASS, or why it was flagged).' },
  { key: 'mlProb', label: 'ML', help: 'ML reject probability (ML_PROB); higher = more likely a bad call.' },
  { key: 'gt', label: 'GT', help: 'Proband genotype (e.g. 0/1 het, 1/1 hom-alt). Sorts by alt-allele dosage.' },
  { key: 'vaf', label: 'VAF', help: 'Proband variant allele fraction (ALT reads / total).' },
  { key: 'dp', label: 'DP', help: 'Proband read depth at this site.' },
  { key: 'gnomad', label: 'gnomAD', help: 'gnomAD genomes population allele frequency (AF_genomes).' },
  {
    key: null,
    label: 'SR/LR',
    help: 'Short-read vs long-read VAF mini-bars (green agree / amber disagree), from FORMAT SAD/LAD.',
  },
  { key: null, label: 'v20', help: 'v20 panel allele frequency (INFO/AF_v20).' },
  { key: null, label: 'STR', help: 'Short tandem repeat region flag (INFO/STR).' },
]

const COLUMN_HELP: ColumnHelp[] = COLUMNS.map((c) => ({ label: c.label, help: c.help }))

export type VariantTableProps = {
  variants: Variant[]
  selectedVariantId: string | null
  onSelect: (variantId: string) => void
  onOpenInIgv?: (locus: string) => void
  sort: VariantSort
  onSortChange: (key: SortKey) => void
  hasMore: boolean
  loading: boolean
  onLoadMore: () => void
}

function filterPillClass(filter: string): string {
  if (filter === 'PASS') return 'bg-green-100 text-green-800'
  if (filter === '.' || filter === '') return 'bg-gray-100 text-gray-600'
  return 'bg-amber-100 text-amber-800'
}

function probandStats(v: Variant): { gt: string; vaf: string; dp: string } {
  const name = probandSampleName(v)
  const s = name ? v.samples[name] : undefined
  const vaf = sampleVaf(s)
  const dp = typeof s?.DP === 'number' ? s.DP : null
  return {
    gt: formatGT(s?.GT),
    vaf: vaf === null ? '-' : vaf.toFixed(2),
    dp: dp === null ? '-' : String(dp),
  }
}

function formatAf(af: number | null): string {
  if (af === null) return '-'
  if (af === 0) return '0'
  return af < 0.001 ? af.toExponential(1) : af.toPrecision(2)
}

const VariantTable: React.FC<VariantTableProps> = ({
  variants,
  selectedVariantId,
  onSelect,
  onOpenInIgv,
  sort,
  onSortChange,
  hasMore,
  loading,
  onLoadMore,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  // Custom, always-visible horizontal scrollbar (GEN-3298). Native scrollbars are
  // overlay-style on macOS and stay hidden until you actively scroll, so analysts
  // didn't realize the table scrolls sideways to off-screen columns. We render
  // our own track + thumb and drive the body's scrollLeft from it.
  const [hbar, setHbar] = useState({ widthPct: 100, leftPct: 0, scrollable: false })
  const dragRef = useRef<{ startX: number; startLeft: number } | null>(null)

  const rowVirtualizer = useVirtualizer({
    count: variants.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  })

  // Recompute the thumb size/position from the body's current horizontal metrics.
  const updateHbar = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { clientWidth: cw, scrollWidth: sw, scrollLeft: sl } = el
    if (sw <= cw + 1) {
      setHbar({ widthPct: 100, leftPct: 0, scrollable: false })
      return
    }
    setHbar({ widthPct: (cw / sw) * 100, leftPct: (sl / sw) * 100, scrollable: true })
  }, [])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    updateHbar()
    if (!hasMore || loading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) onLoadMore()
  }, [hasMore, loading, onLoadMore, updateHbar])

  // Keep the bar accurate on mount, when rows change width, and on resize.
  useEffect(() => {
    updateHbar()
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(updateHbar)
    ro.observe(el)
    return () => ro.disconnect()
  }, [updateHbar, variants.length])

  // Drag the thumb -> scroll the body proportionally (body stays the source of
  // truth; its onScroll handler then refreshes the thumb).
  const onThumbPointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current
    if (!el) return
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft }
    const onMove = (ev: PointerEvent) => {
      const body = scrollRef.current
      const track = trackRef.current
      const drag = dragRef.current
      if (!body || !track || !drag) return
      const ratio = body.scrollWidth / (track.clientWidth || 1)
      body.scrollLeft = drag.startLeft + (ev.clientX - drag.startX) * ratio
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  // Click the track (not the thumb) -> center the view on the click position.
  const onTrackPointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current
    const track = trackRef.current
    if (!el || !track || e.target !== track) return
    const rect = track.getBoundingClientRect()
    const clickRatio = (e.clientX - rect.left) / (rect.width || 1)
    el.scrollLeft = clickRatio * el.scrollWidth - el.clientWidth / 2
  }, [])

  // Keyboard up/down moves the selection through the visible (sorted) list.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      e.preventDefault()
      const idx = variants.findIndex((v) => v.variant_id === selectedVariantId)
      const nextIdx = e.key === 'ArrowDown' ? Math.min(idx + 1, variants.length - 1) : Math.max(idx - 1, 0)
      const next = variants[nextIdx]
      if (next) {
        onSelect(next.variant_id)
        rowVirtualizer.scrollToIndex(nextIdx, { align: 'auto' })
      }
    },
    [variants, selectedVariantId, onSelect, rowVirtualizer]
  )

  const sortIndicator = (key: SortKey | null) => {
    if (!key || sort.key !== key) return ''
    return sort.dir === 'asc' ? ' ▲' : ' ▼'
  }

  const items = rowVirtualizer.getVirtualItems()

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="variant-table">
      <div className="border-border-subtle flex items-center justify-end border-b px-2 py-1">
        <ColumnLegendButton items={COLUMN_HELP} />
      </div>
      <div className="border-border-subtle shrink-0 border-b px-2 py-1.5" aria-hidden="true">
        <div
          ref={trackRef}
          onPointerDown={onTrackPointerDown}
          className="relative h-2.5 w-full cursor-pointer rounded-full bg-slate-200"
          data-testid="variant-table-hscroll"
        >
          <div
            onPointerDown={onThumbPointerDown}
            className={`absolute top-0 h-2.5 rounded-full transition-colors ${
              hbar.scrollable ? 'cursor-grab bg-slate-400 hover:bg-slate-500 active:cursor-grabbing' : 'bg-slate-300'
            }`}
            style={{ width: `${hbar.widthPct}%`, left: `${hbar.leftPct}%` }}
            data-testid="variant-table-hscroll-thumb"
          />
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className="min-h-0 flex-1 overflow-auto outline-none"
        data-testid="variant-table-scroll"
        role="grid"
        aria-rowcount={variants.length}
      >
        <div style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
          <div
            className="text-text-subtle border-border-subtle sticky top-0 z-10 grid items-center border-b bg-white px-3 py-2 text-xs font-semibold tracking-wide uppercase"
            style={{ gridTemplateColumns: GRID_TEMPLATE }}
          >
            {COLUMNS.map((col) => (
              <button
                key={col.label}
                type="button"
                title={col.key ? `${col.help} (click to sort)` : col.help}
                disabled={!col.key}
                onClick={() => col.key && onSortChange(col.key)}
                className={`truncate text-left ${col.key ? 'hover:text-text-heading cursor-pointer' : 'cursor-default'}`}
              >
                {col.label}
                {sortIndicator(col.key)}
              </button>
            ))}
          </div>

          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}>
            {items.map((vi) => {
              const v = variants[vi.index]
              if (!v) return null
              const selected = v.variant_id === selectedVariantId
              const stats = probandStats(v)
              const tag = variantTag(v)
              const mlProb = infoNumber(v, 'ML_PROB')
              const gnomad = infoNumber(v, 'AF_genomes')
              const probandName = probandSampleName(v)
              const probandSample = probandName ? v.samples[probandName] : undefined
              const v20 = infoNumber(v, 'AF_v20')
              const isStr = infoFlag(v, 'STR')
              return (
                <div
                  key={v.variant_id}
                  data-testid="variant-row"
                  data-selected={selected}
                  role="row"
                  aria-selected={selected}
                  onClick={() => onSelect(v.variant_id)}
                  className={`grid cursor-pointer items-center px-3 text-xs ${
                    selected ? 'bg-brand-50 ring-brand-300 ring-1 ring-inset' : vi.index % 2 ? 'bg-gray-50' : 'bg-white'
                  } hover:bg-brand-50/60`}
                  style={{
                    gridTemplateColumns: GRID_TEMPLATE,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${ROW_HEIGHT}px`,
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  <span className="text-text-heading flex items-center gap-1 truncate font-mono">
                    {onOpenInIgv && (
                      <button
                        type="button"
                        title="Open in IGV"
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpenInIgv(variantLocus(v))
                        }}
                        className="text-brand-600 hover:text-brand-800 shrink-0"
                        data-testid="variant-row-igv"
                      >
                        ◎
                      </button>
                    )}
                    <span className="truncate">{variantLocus(v)}</span>
                  </span>
                  <span className="truncate" data-testid="variant-row-tag">
                    {tag ? (
                      <span
                        title={tag}
                        className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${tagBadgeClass(tag)}`}
                      >
                        {tagShortLabel(tag)}
                      </span>
                    ) : (
                      <span className="text-text-subtle">-</span>
                    )}
                  </span>
                  <span className="truncate font-mono">
                    {v.ref}
                    <span className="text-text-subtle">{'>'}</span>
                    {v.alt.join(',')}
                  </span>
                  <span className="text-text-subtle tabular-nums">{v.qual === null ? '-' : v.qual.toFixed(1)}</span>
                  <span className="truncate">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${filterPillClass(v.filter[0] ?? '')}`}
                    >
                      {v.filter[0] ?? '-'}
                    </span>
                  </span>
                  <span className="text-text-subtle tabular-nums">{mlProb === null ? '-' : mlProb.toFixed(2)}</span>
                  <span className="font-mono tabular-nums">{stats.gt}</span>
                  <span className="tabular-nums">{stats.vaf}</span>
                  <span className="tabular-nums">{stats.dp}</span>
                  <span className="text-text-subtle tabular-nums">{formatAf(gnomad)}</span>
                  <span data-testid="variant-row-hybrid">
                    <HybridEvidenceCell sr={srVaf(probandSample)} lr={lrVaf(probandSample)} />
                  </span>
                  <span className="text-text-subtle tabular-nums">{formatAf(v20)}</span>
                  <span className="text-center">
                    {isStr ? (
                      <span
                        title="Short tandem repeat region (INFO/STR)"
                        data-testid="variant-row-str"
                        className="rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium text-purple-800"
                      >
                        STR
                      </span>
                    ) : (
                      <span className="text-text-subtle">-</span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
          {loading && (
            <p className="text-text-subtle p-3 text-center text-xs" data-testid="variant-table-loading">
              Loading variants...
            </p>
          )}
          {!loading && variants.length === 0 && (
            <p className="text-text-subtle p-6 text-center text-sm" data-testid="variant-table-empty">
              No variants match the current filters.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default VariantTable
